import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { endpoints } from "../lib/api";

/** A recorded meeting's name, edited in place: Enter or Save keeps it, Esc
 * or Cancel leaves it as it was. Used on the meeting's page and in the
 * Meetings list. */
export function RenameMeeting({ sessionId, title, csrfToken, onDone, large }: {
  sessionId: string;
  title: string;
  csrfToken: string;
  onDone: () => void;
  /** The meeting page's heading size, rather than a list row's. */
  large?: boolean;
}) {
  const queryClient = useQueryClient();
  const [value, setValue] = useState(title);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    input.current?.focus();
    input.current?.select();
  }, []);

  const rename = useMutation({
    mutationFn: (next: string) => endpoints.renameMeeting(sessionId, next, csrfToken),
    onSuccess: () => {
      // Everywhere the name shows: the meeting, the lists, projects.
      queryClient.invalidateQueries({ queryKey: ["recording", sessionId] });
      queryClient.invalidateQueries({ queryKey: ["recordings"] });
      queryClient.invalidateQueries({ queryKey: ["meetings"] });
      queryClient.invalidateQueries({ predicate: (q) => String(q.queryKey[0]).startsWith("project") });
      onDone();
    },
  });

  function save() {
    const next = value.trim();
    if (!next || next === title) onDone();
    else rename.mutate(next);
  }

  return (
    <form
      className={`rename${large ? " large" : ""}`}
      onSubmit={(event) => {
        event.preventDefault();
        save();
      }}
      // Clicks here mustn't reach the row's link underneath.
      onClick={(event) => event.stopPropagation()}
    >
      <input ref={input} value={value} maxLength={200} aria-label="Meeting name" disabled={rename.isPending}
             onChange={(event) => setValue(event.target.value)}
             onKeyDown={(event) => {
               if (event.key === "Escape") {
                 event.preventDefault();
                 onDone();
               }
             }} />
      <button className="btn-primary" disabled={rename.isPending || !value.trim()}>
        {rename.isPending ? "Saving…" : "Save"}
      </button>
      <button type="button" className="btn-quiet" onClick={onDone} disabled={rename.isPending}>Cancel</button>
      {rename.error && <span className="rename-error">{(rename.error as Error).message}</span>}
    </form>
  );
}

export function IconPencil() {
  return (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M5 19l1-4L16 5l3 3L9 18z" />
    </svg>
  );
}
